/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { SvgHandler } from '../../svg-handler/svg-handler';
import defaultAuthorIcon from '../../../../assets/icons/default-author.svg';
import editIcon from '../../../../assets/icons/edit.svg';

import './user-profile.m.css';
import { Button } from '../login/atoms/button';
import { PROFILE_PAGE_HEADLINE, QUILL_EMPTY_STRING } from '../../../constants';
import { UserScore } from './user-score';

const UserAvatar = ({ avatarUrl }) => {
  if (avatarUrl) {
    return <img styleName="profile-image" src={avatarUrl} loading="lazy" alt="user_avatar" />;
  }
  return (
    <SvgHandler
      styleName="profile-image"
      data-test-id="profile-default-image"
      xlinkHref={defaultAuthorIcon}
      width="125"
      height="125"
      viewBox="0 0 125px 125px"
    />
  );
};

UserAvatar.propTypes = {
  avatarUrl: PropTypes.string
};

const UserProfile = ({ member, updateMember, profileConfig = {}, isDarkModeEnabled, invokedFrom }) => {
  const isInvokedFromPB = invokedFrom === 'PB';
  const imageInputField = useRef(null);
  const nameInputField = useRef(null);
  const [memberName, setMemberName] = useState(isInvokedFromPB ? 'Sudarshan M.S' : member?.name);
  const [memberImage, setMemberImage] = useState(member?.['avatar-url'] || '');
  const [profileImageTempKey, setProfileImageTempKey] = useState(member?.['avatar-url']);
  const [nameInputEditable, setNameInputEditable] = useState(isInvokedFromPB ? true : false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const title =
    profileConfig?.pageTitle && profileConfig?.pageTitle !== QUILL_EMPTY_STRING
      ? profileConfig?.pageTitle
      : PROFILE_PAGE_HEADLINE;

  useEffect(() => {
    if (!saveSuccess) return;
    const updatedMember = { ...member, 'avatar-url': memberImage };
    updateMember(updatedMember);
  }, [saveSuccess]);

  // Dark and light font styling
  const darkStyle = {
    color: '#ffffff'
  };
  const lightStyle = {
    color: '#000000'
  };

  const updateUserDetails = async () => {
    if (isInvokedFromPB) {
      return;
    }
    try {
      let payload = {};
      if (memberName !== member?.name) {
        payload = { ...payload, name: memberName };
      }
      if (profileImageTempKey !== member?.['avatar-url']) {
        payload = { ...payload, 'temp-s3-key': profileImageTempKey };
      }
      if (Object.keys(payload).length > 0) {
        const response = await fetch('/api/auth/v1/users/profile', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...payload
          })
        });
        const data = await response.json();
        if (data?.user) {
          updateMember(data?.user);
        }
        if (!response.ok) {
          setNameInputEditable(false);
          return;
        }
        setSaveSuccess(true);
      }
      setNameInputEditable(false);
    } catch (e) {
      setNameInputEditable(false);
      console.log(e);
    }
  };

  const getFormData = ({ signature, binary }) => {
    const formData = new FormData();
    formData.append('key', signature['key']);
    formData.append('Content-Type', signature['Content-Type']);
    formData.append('success_action_status', signature['success_action_status']);
    formData.append('policy', signature['policy']);
    formData.append('AWSAccessKeyId', signature['AWSAccessKeyId']);
    formData.append('signature', signature['signature']);
    formData.append('acl', signature['acl']);
    formData.append('file', binary);
    return formData;
  };

  const signFile = async (file) => {
    try {
      const signature = await (
        await fetch(`/api/sign?file-name=${file?.name}&&mime-type=${file?.type}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        })
      ).json();
      setProfileImageTempKey(signature['key']);
      // const binary = await getBinaryFromFile(file);
      const formData = getFormData({ signature, binary: file });
      await uploadFileDetails(signature.action, formData);
    } catch (e) {
      console.log(e);
    }
  };

  const uploadFileDetails = async (url, formData) => {
    try {
      await fetch(url, {
        method: 'POST',
        body: formData
      });
    } catch (e) {
      console.log(e);
    }
  };

  const discardChanges = () => {
    if (isInvokedFromPB) {
      return;
    }
    setMemberName(member?.name);
    setMemberImage(member?.['avatar-url']);
    setNameInputEditable(false);
  };

  const openSystemUpload = () => {
    if (isInvokedFromPB) {
      return;
    }
    imageInputField.current.click();
  };

  const handleImageUpload = async (file) => {
    setMemberImage(URL.createObjectURL(file));
    await signFile(file);
  };

  return (
    <div styleName={`profile-wrapper ${isDarkModeEnabled ? 'dark' : ''}`}>
      <h1
        data-test-id="profile-page-title"
        dangerouslySetInnerHTML={{ __html: title }}
        style={isDarkModeEnabled ? { ...darkStyle } : {}}
      />
      <div styleName="image-wrapper">
        <UserAvatar avatarUrl={memberImage} />
        <div
          data-test-id="profile-image-edit-icon"
          styleName={memberImage ? 'image-edit' : 'svg-edit'}
          onClick={openSystemUpload}
          style={isDarkModeEnabled ? { ...lightStyle } : {}}
        >
          <SvgHandler xlinkHref={editIcon} width="25" height="25" viewBox="0 0 25 25" />
        </div>
      </div>
      <input
        type="file"
        id="myFile"
        name="filename"
        ref={imageInputField}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={(e) => handleImageUpload(e.target.files[0])}
      />
      <div>
        <label data-test-id="profile-name-label">{profileConfig?.nameInputLabel || 'Name'}</label>
        <input
          data-test-id="profile-name-input"
          type="text"
          id="name"
          ref={nameInputField}
          placeholder="Enter your name"
          readOnly={isInvokedFromPB || !nameInputEditable}
          styleName={!nameInputEditable ? 'name-input' : ''}
          value={memberName}
          onChange={(e) => setMemberName(e.target.value)}
        />
        {!nameInputEditable && (
          <div
            data-test-id="profile-name-edit-icon"
            styleName="edit-name-icon"
            onClick={() => {
              setNameInputEditable(true);
              nameInputField.current.focus();
            }}
          >
            <SvgHandler xlinkHref={editIcon} width="18" height="18" viewBox="0 0 18 18" />
          </div>
        )}
        <br />
        <label data-test-id="profile-email-label">{profileConfig?.emailInputLabel || 'Email'}</label>
        <input
          data-test-id="profile-email-input"
          type="text"
          id="name"
          placeholder="abc@mail.com"
          readOnly
          value={
            isInvokedFromPB
              ? 'abc@mail.com'
              : member.email || member?.['phone-number'] || member?.['login-phone-number']
          }
        />
        {(nameInputEditable || (memberImage && member?.['avatar-url'] !== memberImage)) && (
          <>
            <Button
              data-test-id="profile-save-btn"
              text={profileConfig?.saveButtonLabel || 'Save'}
              clickHandler={() => updateUserDetails()}
              enableEnterClick={true}
            />
            <br />
            <button
              data-test-id="profile-discard-btn"
              onClick={discardChanges}
              style={isDarkModeEnabled ? { ...darkStyle } : {}}
            >
              {profileConfig?.cancelButtonLabel || 'Discard Changes'}
            </button>
          </>
        )}
        <UserScore isDarkModeEnabled={isDarkModeEnabled} />
      </div>
    </div>
  );
};

UserProfile.propTypes = {
  isAccessTypeLoaded: PropTypes.bool,
  member: PropTypes.shape({
    name: PropTypes.string,
    'avatar-url': PropTypes.string,
    email: PropTypes.string,
    'phone-number': PropTypes.string
  }),
  localizedSubscribe: PropTypes.string,
  profileConfig: PropTypes.shape({
    pageTitle: PropTypes.string,
    nameInputLabel: PropTypes.string,
    emailInputLabel: PropTypes.string,
    saveButtonLabel: PropTypes.string,
    cancelButtonLabel: PropTypes.string
  }),
  invokedFrom: PropTypes.string,
  isDarkModeEnabled: PropTypes.bool
};

export { UserProfile };
